/**
 * Shared bits for the full-window emotes (see ../emotes.ts): a canvas over
 * the whole window, frame timing, and a few random helpers.
 */

/** The party palette, the same one confetti uses. */
export const COLORS = ["#f2c446", "#e8604c", "#3f9a6b", "#4a7fd6", "#b06fd8", "#f08fb0", "#2fb3b0"];

/** Whether to tone it down: fewer pieces, less movement. */
export const calm = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

export const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);
export const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];

export interface Frame {
  ctx: CanvasRenderingContext2D;
  /** The window's size in CSS pixels. */
  w: number;
  h: number;
  /** Milliseconds since the scene started. A backgrounded tab picks up where it left off rather than skipping ahead. */
  t: number;
  /** This frame's length: 1 is one 60 Hz frame, capped at 3. */
  dt: number;
}

/** Nothing plays for longer than this, whatever `draw` says. */
const MAX_MS = 20_000;

/**
 * A canvas over the whole window, under the banners and above everything
 * else, that ignores the mouse. Calls `draw` every frame, on a cleared canvas
 * unless `clear` is false, until it returns false; then the canvas goes and
 * the promise resolves.
 */
export function scene(draw: (f: Frame) => boolean, { clear = true }: { clear?: boolean } = {}): Promise<void> {
  const canvas = document.createElement("canvas");
  canvas.className = "emote-canvas";
  canvas.setAttribute("aria-hidden", "true");
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d")!;
  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = innerWidth * dpr;
    canvas.height = innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);

  return new Promise((done) => {
    let t = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const ms = Math.min(now - last, 50);
      last = now;
      t += ms;
      if (clear) ctx.clearRect(0, 0, innerWidth, innerHeight);
      const more = draw({ ctx, w: innerWidth, h: innerHeight, t, dt: ms / 16.67 || 1 });
      if (more && t < MAX_MS) requestAnimationFrame(tick);
      else {
        window.removeEventListener("resize", resize);
        canvas.remove();
        done();
      }
    };
    requestAnimationFrame(tick);
  });
}
